import { useSearchParams } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import { formatShortDate } from '@odo/shared-ui';
import type { Appointment } from '../../../services/appointments';
import type { PatientListItemType } from '../../../providers/patient/PatientsListProvider';
import {
    APPOINTMENT_PATIENT_QUERY_PARAM,
    APPOINTMENT_QUERY_PARAM,
    NEW_APPOINTMENT_PARAM
} from '../../appointments/AppointmentsModal';
import { APPOINTMENTS_QUERY_OPEN, APPOINTMENTS_QUERY_PARAM } from '../../../utils/constants';

interface NextAppointmentCellProps {
    patient: PatientListItemType;
    /** Soonest upcoming appointment for this patient, or null when none is scheduled. */
    nextAppointment: Appointment | null;
    /** While true, a null `nextAppointment` means "not known yet" rather than
     *  "none scheduled" - so the schedule button stays hidden instead of
     *  flashing on every row and then turning into a date. */
    appointmentsLoading: boolean;
}

/**
 * Deep-links into the shared appointments modal (owned by the page) via URL
 * params, rather than a callback prop, so it inherits the modal's
 * push-on-open contract: Back closes it again.
 */
export function NextAppointmentCell({
    patient,
    nextAppointment,
    appointmentsLoading
}: NextAppointmentCellProps) {
    const [, setSearchParams] = useSearchParams();

    const openNewAppointment = (patientId: string) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set(APPOINTMENTS_QUERY_PARAM, APPOINTMENTS_QUERY_OPEN);
            next.set(APPOINTMENT_QUERY_PARAM, NEW_APPOINTMENT_PARAM);
            next.set(APPOINTMENT_PATIENT_QUERY_PARAM, patientId);
            return next;
        });
    };

    const openExistingAppointment = (appointmentId: string) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set(APPOINTMENTS_QUERY_PARAM, APPOINTMENTS_QUERY_OPEN);
            next.set(APPOINTMENT_QUERY_PARAM, appointmentId);
            next.delete(APPOINTMENT_PATIENT_QUERY_PARAM);
            return next;
        });
    };

    return (
        <span className="block truncate">
            {nextAppointment ? (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        openExistingAppointment(nextAppointment.id);
                    }}
                    title={`View appointment for ${patient.name ?? 'this patient'}`}
                    className="underline decoration-gray-300 hover:decoration-gray-500 text-gray-700 hover:text-gray-900 cursor-pointer transition-colors"
                >
                    {formatShortDate(nextAppointment.date)}
                </button>
            ) : appointmentsLoading || !patient.id ? (
                <span className="text-gray-500">–</span>
            ) : (
                <button
                    type="button"
                    onClick={() => openNewAppointment(patient.id)}
                    title={`Schedule an appointment for ${patient.name ?? 'this patient'}`}
                    aria-label={`Schedule an appointment for ${patient.name ?? 'this patient'}`}
                    className="mx-auto flex items-center justify-center w-7 h-7 rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                >
                    <PlusIcon className="h-4 w-4" />
                </button>
            )}
        </span>
    );
}
